import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, List, Tag, Spin, Typography, Descriptions, Badge, Empty, message, Button } from 'antd';
import {
  UserOutlined, BankOutlined, TeamOutlined, CalendarOutlined,
  BookOutlined, ClockCircleOutlined, CheckCircleOutlined,
  EditOutlined, TrophyOutlined, FileTextOutlined,
  ExclamationCircleOutlined, PlusOutlined,
} from '@ant-design/icons';
import { authApi, courseApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text } = Typography;

const roleDisplayMap: Record<string, string> = {
  admin: '系统管理员',
  teacher: '教师',
  dean: '院长',
  student: '学生',
  deputydean: '副院长',
  sci_assistant: '少科院助理',
};

const AdminDashboard: React.FC<{ data: any }> = ({ data }) => (
  <>
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={6}>
        <Card><Statistic title="用户总数" value={data.total_users} prefix={<UserOutlined />} styles={{ content: { color: '#16a34a' } }} /></Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card><Statistic title="学院数量" value={data.total_branches} prefix={<BankOutlined />} styles={{ content: { color: '#52c41a' } }} /></Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card><Statistic title="在籍学生" value={data.enrolled_students} prefix={<TeamOutlined />} styles={{ content: { color: '#722ed1' } }} /></Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card><Statistic title="今日活动" value={data.today_activities} prefix={<CalendarOutlined />} styles={{ content: { color: '#fa8c16' } }} /></Card>
      </Col>
    </Row>
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col xs={24} md={12}>
        <Card title="最近操作日志" size="small">
          <List
            dataSource={data.recent_logs?.slice(0, 5) || []}
            renderItem={(item: any) => {
              const desc = item.description?.startsWith(item.operator)
                ? item.description.slice(item.operator.length)
                : item.description;
              return (
                <List.Item style={{ padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.created_at ? `${item.created_at.slice(0, 4)}年${item.created_at.slice(5, 7)}月${item.created_at.slice(8, 10)}日 ${item.created_at.slice(11, 16)}` : '-'}
                    {' '}{item.operator}{desc}
                  </Text>
                </List.Item>
              );
            }}
          />
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title="角色分布" size="small">
          <Row gutter={8}>
            {data.role_distribution?.map((r: any) => (
              <Col key={r.role} span={12} style={{ marginBottom: 8 }}>
                <Tag color="blue">{roleDisplayMap[r.role] || r.role}: {r.count}人</Tag>
              </Col>
            ))}
          </Row>
        </Card>
      </Col>
    </Row>
  </>
);

const TeacherDashboard: React.FC<{ data: any; navigate: any }> = ({ data, navigate }) => {
  const activityTypeColor: Record<string, string> = { study_tour: 'blue', summer_camp: 'orange', winter_camp: 'cyan', competition: 'purple', seminar: 'green' };

  return (
    <div>
      {/* ── 第一行：基本信息 5 项并排 ── */}
      <Row gutter={[12, 12]}>
        <Col span={5}>
          <Card><Statistic title="我的课程" value={data.my_courses_count ?? 0} suffix={data.my_courses_count ? '门' : ''} prefix={<BookOutlined />} styles={{ content: { color: '#722ed1' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="学生总数" value={data.total_students ?? 0} suffix="人" prefix={<TeamOutlined />} styles={{ content: { color: '#1677ff' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="待批作业" value={data.total_pending_homework ?? 0} suffix={data.total_pending_homework ? '项' : ''} prefix={<ExclamationCircleOutlined />} styles={{ content: { color: data.total_pending_homework > 0 ? '#ff4d4f' : '#52c41a' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="待审课题" value={data.total_pending_research ?? 0} suffix={data.total_pending_research ? '项' : ''} prefix={<FileTextOutlined />} styles={{ content: { color: data.total_pending_research > 0 ? '#fa8c16' : '#52c41a' } }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="待录成绩" value={data.pending_grade_batches ?? 0} suffix={data.pending_grade_batches ? '批' : ''} prefix={<EditOutlined />} styles={{ content: { color: data.pending_grade_batches > 0 ? '#ff4d4f' : '#52c41a' } }} /></Card>
        </Col>
      </Row>

      {/* ── 第二行：活动统计 + 待办事项 ── */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title={<><CalendarOutlined style={{ marginRight: 6 }} />活动组织</>} size="small">
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Statistic title="今日" value={data.activity_stats?.today ?? 0} styles={{ content: { fontSize: 18, color: '#1890ff' } }} />
              </Col>
              <Col span={8}>
                <Statistic title="本周" value={data.activity_stats?.this_week ?? 0} styles={{ content: { fontSize: 18, color: '#52c41a' } }} />
              </Col>
              <Col span={8}>
                <Statistic title="总计" value={data.activity_stats?.total ?? 0} styles={{ content: { fontSize: 18, color: '#8c8c8c' } }} />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={<><EditOutlined style={{ marginRight: 6 }} />待办事项</>} size="small">
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Statistic
                  title="待录成绩批次"
                  value={data.pending_grade_batches ?? 0}
                  styles={{ content: { fontSize: 18, color: data.pending_grade_batches > 0 ? '#ff4d4f' : undefined } }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="待批作业总数"
                  value={data.total_pending_homework ?? 0}
                  styles={{ content: { fontSize: 18, color: data.total_pending_homework > 0 ? '#fa8c16' : undefined } }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ── 第三行：我的课程列表 ── */}
      {data.my_courses?.length > 0 && (
        <Card
          title={<><BookOutlined style={{ marginRight: 6 }} />我的课程 ({data.my_courses.length})</>}
          size="small"
          style={{ marginTop: 16 }}
        >
          <List
            dataSource={data.my_courses}
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
            renderItem={(course: any) => (
              <List.Item>
                <Card
                  size="small"
                  hoverable
                  style={{ marginBottom: 0 }}
                  onClick={() => navigate(`/courses/${course.id}`)}
                >
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>{course.name}</Text>
                  {course.year && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{course.year} · {course.semester_display}</Text>
                  )}
                  {course.branch_name && (
                    <Tag style={{ marginTop: 6, fontSize: 12 }}>{course.branch_name}</Tag>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>学生：{course.student_count}人</Text>
                  </div>
                  {course.pending_homework_count > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <Badge count={course.pending_homework_count} size="small" offset={[0, 0]}>
                        <Tag color="red" style={{ fontSize: 12 }}>待批作业：{course.pending_homework_count}</Tag>
                      </Badge>
                    </div>
                  )}
                  {course.pending_research_count > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <Badge count={course.pending_research_count} size="small" offset={[0, 0]}>
                        <Tag color="orange" style={{ fontSize: 12 }}>待审课题：{course.pending_research_count}</Tag>
                      </Badge>
                    </div>
                  )}
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* ── 第四行：近期组织的活动 ── */}
      {data.recent_activities?.length > 0 && (
        <Card
          title={<><TrophyOutlined style={{ marginRight: 6 }} />近期组织的活动 ({data.recent_activities.length})</>}
          size="small"
          style={{ marginTop: 16 }}
        >
          <List
            dataSource={data.recent_activities}
            renderItem={(item: any) => (
              <List.Item actions={[
                item.status === 'ongoing' ? <Badge status="processing" text="进行中" /> :
                item.status === 'ended' ? <Badge text="已结束" /> :
                <Badge status="default" text={item.status_display || item.status} />,
              ]}>
                <List.Item.Meta
                  avatar={
                    <Tag color={activityTypeColor[item.activity_type] || 'default'} style={{ borderRadius: 4 }}>
                      {item.activity_type_display || item.activity_type}
                    </Tag>
                  }
                  title={<Text>{item.title}</Text>}
                  description={
                    <span>
                      {item.start_date && `${item.start_date}`}
                      {item.end_date && item.end_date !== item.start_date ? ` ~ ${item.end_date}` : ''}
                      {item.location && ` · ${item.location}`}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
};

const StudentDashboard: React.FC<{ data: any; navigate: any; onRefresh: () => void }> = ({ data, navigate, onRefresh }) => {
  const actSummary = data.activities_summary || {};
  const atts = data.attendances || {};
  const abilityMap: Record<string, string> = { excellent: '优秀', good: '良好', normal: '一般' };
  const activityTypeColor: Record<string, string> = { study_tour: 'blue', summer_camp: 'orange', winter_camp: 'cyan' };

  // 报名操作
  const [enrollingId, setEnrollingId] = useState<number | null>(null);
  const handleEnroll = async (courseId: number) => {
    setEnrollingId(courseId);
    try {
      await courseApi.enrollments.create({ course: courseId });
      message.success('报名成功');
      onRefresh();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '报名失败');
    } finally {
      setEnrollingId(null);
    }
  };

  return (
    <div>
      {/* ── 第一行：基本信息 5 项并排 ── */}
      <Row gutter={[12, 12]}>
        <Col span={5}>
          <Card><Statistic title="所属学院" value={data.branch || '-'} prefix={<BankOutlined />} styles={{ content: { color: '#1677ff' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="入院年份" value={data.admission_year || '-'} prefix={<CalendarOutlined />} styles={{ content: { color: '#52c41a' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="待完成作业" value={data.pending_homework_total ?? 0} suffix={data.pending_homework_total ? '项' : ''} prefix={<ExclamationCircleOutlined />} styles={{ content: { color: data.pending_homework_total > 0 ? '#ff4d4f' : '#52c41a' } }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="学习课程" value={data.my_courses_count ?? 0} suffix={data.my_courses_count ? '门' : ''} prefix={<BookOutlined />} styles={{ content: { color: '#722ed1' } }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="参加活动" value={actSummary.total_joined ?? 0} suffix={actSummary.total_joined ? '场' : ''} prefix={<TrophyOutlined />} styles={{ content: { color: '#fa8c16' } }} /></Card>
        </Col>
      </Row>

      {/* ── 第二行：活动统计详情 + 能力评级 ── */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title={<><ClockCircleOutlined style={{ marginRight: 6 }} />活动参与</>} size="small">
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Statistic title="进行中" value={actSummary.ongoing ?? 0} styles={{ content: { fontSize: 18, color: '#1890ff' } }} />
              </Col>
              <Col span={8}>
                <Statistic title="已结束" value={actSummary.ended ?? 0} styles={{ content: { fontSize: 18, color: '#8c8c8c' } }} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="待提交"
                  value={data.pending_submissions ?? 0}
                  styles={{ content: { fontSize: 18, color: data.pending_submissions > 0 ? '#ff4d4f' : undefined } }}
                  suffix={
                    data.pending_submissions > 0 ? (
                      <Badge count={data.pending_submissions} size="small" offset={[2, -4]} />
                    ) : undefined
                  }
                />
              </Col>
            </Row>

            {/* 考勤小计 */}
            {(atts.present || atts.absent || atts.leave) && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>考勤记录：</Text>{' '}
                {atts.present && <Tag color="green">出勤 {atts.present}</Tag>}
                {atts.absent && <Tag color="red">缺席 {atts.absent}</Tag>}
                {atts.leave && <Tag color="orange">请假 {atts.leave}</Tag>}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={<><EditOutlined style={{ marginRight: 6 }} />个人档案</>} size="small">
            <Descriptions column={1} size="small" labelStyle={{ width: 90 }}>
              <Descriptions.Item label="学号">{data.student_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="能力评级">
                {data.ability_level
                  ? <Tag color={data.ability_level === 'excellent' ? 'gold' : data.ability_level === 'good' ? 'blue' : 'default'}>
                      {abilityMap[data.ability_level] || data.ability_level}
                    </Tag>
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="未读通知">
                <Text type={data.unread_notices > 0 ? 'danger' : undefined}>{data.unread_notices || 0} 条</Text>
              </Descriptions.Item>
            </Descriptions>
            {data.skill_tags?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>特长标签：</Text>
                {data.skill_tags.map((tag: string) => <Tag key={tag} color="green">{tag}</Tag>)}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ── 第三行：我的课程列表 ── */}
      {data.my_courses?.length > 0 && (
        <Card
          title={<><FileTextOutlined style={{ marginRight: 6 }} />我的课程 ({data.my_courses.length})</>}
          size="small"
          style={{ marginTop: 16 }}
        >
          <List
            dataSource={data.my_courses}
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
            renderItem={(course: any) => (
              <List.Item>
            <Card
              size="small"
              hoverable
              style={{ marginBottom: 0 }}
              onClick={() => navigate(`/my-courses/${course.course_no}`)}
            >
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>{course.name}</Text>
                  {course.year && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{course.year} · {course.semester_display}</Text>
                  )}
                  {course.teacher_name && (
                    <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>教师：{course.teacher_name}</Text></div>
                  )}
                  {course.branch_name && (
                    <Tag style={{ marginTop: 6, fontSize: 12 }}>{course.branch_name}</Tag>
                  )}
                  {course.pending_homework_count > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <Badge count={course.pending_homework_count} size="small" offset={[0, 0]}>
                        <Tag color="red" style={{ fontSize: 12 }}>未完成作业：{course.pending_homework_count}</Tag>
                      </Badge>
                    </div>
                  )}
                </Card>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* ── 第四行：近期活动列表 ── */}
      {actSummary.recent_activities?.length > 0 && (
        <Card
          title={<><TrophyOutlined style={{ marginRight: 6 }} />近期参加的活动 ({actSummary.recent_activities.length})</>}
          size="small"
          style={{ marginTop: 16 }}
        >
          <List
            dataSource={actSummary.recent_activities}
            renderItem={(item: any) => (
              <List.Item actions={[
                item.status === 'ongoing' ? <Badge status="processing" text="进行中" /> :
                item.status === 'ended' ? <Badge text="已结束" /> :
                <Badge status="default" text={item.status_display || item.status} />,
              ]}>
                <List.Item.Meta
                  avatar={
                    <Tag color={activityTypeColor[item.activity_type] || 'default'} style={{ borderRadius: 4 }}>
                      {item.activity_type_display || item.activity_type}
                    </Tag>
                  }
                  title={<a onClick={() => {}}>{item.title}</a>}
                  description={
                    <span>
                      {item.start_date && `${item.start_date}`}
                      {item.end_date && item.end_date !== item.start_date ? ` ~ ${item.end_date}` : ''}
                      {item.location && ` · ${item.location}`}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* ── 可选课程列表 ── */}
      {data.available_courses?.length > 0 && (
        <Card
          title={<><BookOutlined style={{ marginRight: 6 }} />可选课程报名 ({data.available_courses.length})</>}
          size="small"
          style={{ marginTop: 16 }}
        >
          <List
            dataSource={data.available_courses}
            renderItem={(course: any) => (
              <List.Item
                actions={[
                  <Button
                    key="enroll"
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    loading={enrollingId === course.id}
                    onClick={() => handleEnroll(course.id)}
                  >
                    报名
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Text>{course.name}</Text>}
                  description={
                    <span>
                      {course.teacher_name && `教师：${course.teacher_name}`}
                      {course.branch_name && ` · ${course.branch_name}`}
                      {course.year && ` · ${course.year}-${course.year + 1} ${course.semester_display}`}
                      {course.student_count > 0 && ` · ${course.student_count}人已选`}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
};

const DeanDashboard: React.FC<{ data: any }> = ({ data }) => (
  <Row gutter={[16, 16]}>
    <Col xs={24} sm={12} md={6}>
      <Card><Statistic title="在籍学生" value={data.enrolled_students} /></Card>
    </Col>
    <Col xs={24} sm={12} md={6}>
      <Card><Statistic title="待审批录取" value={data.pending_admission} styles={{ content: { color: '#ff4d4f' } }} /></Card>
    </Col>
    <Col xs={24} sm={12} md={6}>
      <Card><Statistic title="待审核成绩" value={data.pending_grade_review} styles={{ content: { color: '#fa8c16' } }} /></Card>
    </Col>
    <Col xs={24} sm={12} md={6}>
      <Card><Statistic title="进行中活动" value={data.ongoing_activities} styles={{ content: { color: '#52c41a' } }} /></Card>
    </Col>
  </Row>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    authApi.getDashboard()
      .then((res) => setData(res.data))
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.message || '加载仪表盘失败';
        message.error(msg);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const roleLabel: Record<string, string> = {
    admin: '系统管理员',
    teacher: '教师',
    dean: '院长',
    deputydean: '副院长',
    student: '学生',
    sci_assistant: '少科院助理',
  };
  // roleDisplayMap 已提升为模块级常量，此处仅保留欢迎语专用映射
  void roleDisplayMap; // 确认模块级常量可用

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          欢迎回来，{user?.real_name} 👋
        </Title>
        <Text type="secondary">角色：{roleLabel[user?.role || 'student']}</Text>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : data ? (
        <>
          {user?.role === 'admin' && <AdminDashboard data={data} />}
          {user?.role === 'teacher' && <TeacherDashboard data={data} navigate={navigate} />}
          {user?.role === 'dean' && <DeanDashboard data={data} />}
          {user?.role === 'student' && <StudentDashboard data={data} navigate={navigate} onRefresh={() => setRefreshKey(k => k + 1)} />}
        </>
      ) : (
        <Empty description="加载失败，请刷新页面重试" />
      )}
    </div>
  );
};

export default DashboardPage;